import React from "react";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";

import CustomTooltip from "components/common/Tooltip";

import { CustomChip } from "./ui";
import { statusColor, titleCase } from "./types";

/**
 * [PRD-3348] Invoice status chip for the admin billing views. Void invoices
 * that billing-service voided on purpose (a pause, a resume re-anchor, a $0
 * total, or the PRD-3315 duplicate charge guard) get a tooltip saying why, so
 * admins don't mistake them for cancellations. The reason sits behind an info
 * icon inside the chip.
 */
export function voidReasonTooltip(metadata?: Record<string, unknown> | null): string | undefined {
  switch (metadata?.voidReason) {
    case "paused_cycle_skipped":
      return "Voided because the subscription was paused on this invoice's due date. The customer was not charged for this month.";
    case "resume_reanchored":
      return "Voided because the subscription was resumed on a new billing period. A new draft replaced this invoice.";
    case "zero_amount":
      return "Voided because the invoice total was $0. Nothing was charged.";
    case "duplicate_charge_blocked": {
      const days = metadata.guardWindowDays;
      if (typeof days === "number" && days > 0) {
        return `Voided to prevent a duplicate charge. The customer already had a recurring charge within the last ${days} ${
          days === 1 ? "day" : "days"
        }.`;
      }
      return "Voided to prevent a duplicate charge. The customer was already charged recently.";
    }
    default:
      return undefined;
  }
}

export default function InvoiceStatusChip({
  status,
  metadata,
}: {
  status: string;
  metadata?: Record<string, unknown> | null;
}) {
  const tooltip = status === "void" ? voidReasonTooltip(metadata) : undefined;

  // Only the info icon carries the tooltip, so the chip itself stays a plain
  // status label. The outer span stops a click or tap on the icon from
  // reaching the table row's onClick (which opens the detail modal), while
  // still letting CustomTooltip's mobile tap toggle run on the inner span.
  // describeChild keeps the aria-label as the name and the reason as its
  // description.
  const endIcon = tooltip ? (
    <span style={{ display: "inline-flex" }} onClick={(event) => event.stopPropagation()} role="presentation">
      <CustomTooltip content={tooltip} describeChild>
        <span
          style={{ display: "inline-flex", cursor: "pointer" }}
          role="button"
          tabIndex={0}
          aria-label="Why was this invoice voided?"
          data-testid="invoice-void-reason-icon"
        >
          <InfoOutlinedIcon aria-hidden sx={{ fontSize: 16, color: "var(--color-icon-secondary)" }} />
        </span>
      </CustomTooltip>
    </span>
  ) : undefined;

  return <CustomChip label={titleCase(status)} size="small" color={statusColor(status)} endIcon={endIcon} />;
}
